export const yelp = {
  id: 'yelp-redesign',
  title: "Cutting Yelp's Search Time in Half",
  subtitle: 'Restructuring hierarchy and placement to get photos and reviews in front of users faster',
  tags: ['Local Business Reviews', 'Mobile', 'UX Design', 'UX Research'],
  description: 'Yelp is a long-standing platform for restaurant discovery and reviews, but user research showed that its mobile experience slowed decision-making.',
  role: 'End-to-end UX design',
  tools: 'Figma Design, Figma Make',
  platform: 'Mobile / iOS & Android',
  year: '2025',

  sections: [
    {
      type: 'hero-images',
      images: [
        { src: '/images/yelp/hero.png', alt: 'Yelp redesign hero' }
      ]
    },
    {
      type: 'two-col-text',
      heading: 'Problem',
      content: [
        'Yelp is the go-to for restaurant reviews — but its mobile experience was losing people before they could decide. Carousels, sponsored content, and buried photos slowed evaluation. Many users defaulted to Google or Apple Maps for speed, treating Yelp as a last resort for deeper detail rather than a first stop.',
        'The core flow that needed fixing: search → photos → review.'
      ]
    },
    {
      type: 'two-col-text',
      heading: 'Research Approach',
      content: [
        'I ran two rounds of A/B testing, comparing the native Yelp app against a redesigned prototype on the same task: find a ramen spot with 4+ stars within 3 miles, evaluate it using photos, and leave a review.',
        'Round 1 identified where time was being lost. Round 2 validated whether the changes actually fixed it. Four friction points came through clearly in Round 1: cluttered search results that slowed scanning, photos buried too deep to find quickly, review prompts that were easy to miss, and inconsistent layout that increased cognitive load across screens.'
      ]
    },
    {
      type: 'callout',
      text: 'Design question: how might Yelp reduce cognitive load in restaurant discovery so users can move from search to decision faster?'
    },
    {
      type: 'two-col-text',
      heading: 'Design Decisions',
      content: [
        "The redesign didn't add anything new — it made what already existed easier to find and use.",
        'The **business detail page** was the primary focus. Horizontal carousels and scattered action buttons were replaced with organized vertical lists and grouped controls, reducing the visual noise that was slowing evaluation.',
        "**Photos** were surfaced higher in the business detail page with larger thumbnails, category labels, and multiple entry points. They'd always been there — they just weren't reachable fast enough.",
        '**Review prompts** for food, service, and ambiance already existed. Making them more visible through spacing and placement was enough to drive richer engagement without changing the flow at all.',
        '**Visual consistency** was standardized across screens — typography, spacing, and component patterns brought into alignment so the interface felt coherent end to end.'
      ]
    },
    {
      type: 'video',
      src: 'https://zalq7ziyrwgol5d7.public.blob.vercel-storage.com/yelp-prototype-demo-compressed.mp4',
      caption: 'Prototype demo',
      phoneSize: true,
    },
    {
      type: 'image-grid',
      columns: 3,
      images: [
        { src: '/images/yelp/search-current.png', alt: 'Home — current', caption: 'Home (current)' },
        { src: '/images/yelp/restaurant-current.png', alt: 'Business — current', caption: 'Business (current)' },
        { src: '/images/yelp/review-current.png', alt: 'Review — current', caption: 'Review (current)' }
      ]
    },
    {
      type: 'image-grid',
      columns: 3,
      images: [
        { src: '/images/yelp/search-proposed.png', alt: 'Home — proposed', caption: 'Home (proposed)' },
        { src: '/images/yelp/restaurant-proposed.png', alt: 'Business — proposed', caption: 'Business (proposed)' },
        { src: '/images/yelp/review-proposed.png', alt: 'Review — proposed', caption: 'Review (proposed)' }
      ]
    },
    {
      type: 'two-col-text',
      heading: 'Results',
      content: [
        'The numbers from Round 2 were clear. Search time dropped from ~40 seconds to ~20 seconds — a 50% improvement. Photo discovery went from ~25 seconds to ~5 seconds, 5× faster. Review prompt engagement increased noticeably, with participants leaving more detailed feedback without being asked to do anything differently.'
      ]
    },
    {
      type: 'callout',
      text: '"Cleaner, clearer, easier to navigate" — how participants described the redesign unprompted.'
    },
    {
      type: 'two-col-text',
      heading: 'Reflection',
      content: [
        'The most interesting finding: every one of these improvements came from adjusting what already existed, not from adding anything new. Yelp already had good photos, useful review prompts, and relevant search results. The problem was hierarchy and placement — not capability.',
        "Competing with Google and Apple Maps doesn't require Yelp to do more. It requires Yelp to surface its depth faster."
      ]
    },
    {
      type: 'image-single',
      image: { src: '/images/yelp/overview.png', alt: 'Yelp redesign — all screens overview', caption: 'All screens overview' }
    }
  ]
};
